/* ============================================================================
 * H Potter — Smart Search Enhancement for Dawn / Online Store 2.0
 * ----------------------------------------------------------------------------
 * Pure theme code. No app required. No dependencies.
 *
 * What it does:
 *   1. Corrects common misspellings before searching        (TYPOS)
 *   2. Maps "we-sell-it-under-another-name" terms to matches (SYNONYMS)
 *   3. Sends clear category / sale intent straight to the right collection
 *      instead of a weak results page                         (REDIRECTS)
 *   4. Normalizes size units so "7 ft" / "7 foot" / "7ft" behave the same
 *   5. Shows a "Showing results for ..." banner when a query was corrected
 *
 * It works by intercepting (a) the predictive-search dropdown network calls
 * and (b) search form submits — so it does NOT depend on Dawn's internal
 * predictive-search.js and will survive theme updates.
 *
 * ALL of the maps below were built from hpotter.com's real on-site search
 * data (the queries shoppers typed that dead-ended). Edit freely — the keys
 * are always lowercase, trimmed search terms.
 * ==========================================================================*/
(function () {
  'use strict';

  /* ------------------------------------------------------------------ CONFIG */

  // Toggle the whole behavior on/off quickly if you ever need to debug.
  var ENABLED = true;

  // Show a small "Showing results for X" note on the results page when a
  // typed query was auto-corrected. Set to false to disable.
  var SHOW_CORRECTION_BANNER = true;

  // --- 1. Misspellings -> correct spelling (matched word-by-word) ----------
  var TYPOS = {
    'trallis': 'trellis',
    'trellise': 'trellis',
    'trelis': 'trellis',
    'trellies': 'trellis',
    'oblesik': 'obelisk',
    'oblisk': 'obelisk',
    'obilisk': 'obelisk',
    'charcol': 'charcoal',
    'charcole': 'charcoal',
    'terarium': 'terrarium',
    'terrarrium': 'terrarium',
    'terrium': 'terrarium',
    'arbour': 'arbor',
    'arbours': 'arbors',
    'planer': 'planter',
    'planers': 'planters',
    'brinze': 'bronze',
    'bronz': 'bronze'
  };

  // --- 2. Synonyms / alternate names -> term that matches the catalog ------
  // Matched as a WHOLE query first, then word-by-word as a fallback.
  var SYNONYMS = {
    'wardian': 'terrarium',
    'wardian case': 'terrarium',
    'arch': 'arbor',
    'arches': 'arbor',
    'archway': 'arbor',
    'plant support': 'trellis',
    'plant supports': 'trellis',
    '3 d plant support': 'trellis',
    '3d plant support': 'trellis',
    'climbing plant support': 'trellis',
    'tomato cage': 'obelisk',
    'plant cage': 'obelisk',
    'tiered plant stand': 'plant stand'
  };
  // Note: specific multi-word queries like "parrot trellis" or
  // "flower pot trellis" are intentionally NOT collapsed to the bare category.
  // Typo correction still fixes them (e.g. "trallis" -> "trellis") and they
  // run as a specific search so a matching product can surface.

  // --- 3. Strong intent -> go straight to the collection (skip results) ----
  // Only verified hpotter.com collection handles are used here.
  // Two groups: SALE intent and CATEGORY intent.
  var REDIRECTS = {
    // Sale / clearance intent -> Warehouse Deals
    'sale': '/collections/warehouse-deals',
    'sales': '/collections/warehouse-deals',
    'on sale': '/collections/warehouse-deals',
    'trellis sale': '/collections/warehouse-deals',
    'obelisk sale': '/collections/warehouse-deals',
    'clearance': '/collections/warehouse-deals',
    'deals': '/collections/warehouse-deals',
    'discount': '/collections/warehouse-deals',
    'warehouse sale': '/collections/warehouse-deals',
    'warehouse sales': '/collections/warehouse-deals',
    'warehouse deals': '/collections/warehouse-deals',

    // Category intent -> the matching collection (well-stocked landing page).
    // Comment any of these out if you'd rather show a search results page.
    'trellis': '/collections/garden-trellis',
    'trellises': '/collections/garden-trellis',
    'garden trellis': '/collections/garden-trellis',
    'metal trellis': '/collections/garden-trellis',
    'wrought iron trellis': '/collections/garden-trellis',
    'wall trellis': '/collections/garden-trellis',
    'obelisk': '/collections/garden-obelisk',
    'obelisks': '/collections/garden-obelisk',
    'garden obelisk': '/collections/garden-obelisk',
    'arbor': '/collections/garden-arbors',
    'arbors': '/collections/garden-arbors',
    'garden arbor': '/collections/garden-arbors',
    'garden arbors': '/collections/garden-arbors',
    'terrarium': '/collections/terrariums',
    'terrariums': '/collections/terrariums',
    'planter': '/collections/tall-indoor-outdoor-planters',
    'planters': '/collections/tall-indoor-outdoor-planters',
    'tall planter': '/collections/tall-indoor-outdoor-planters',
    'tall planters': '/collections/tall-indoor-outdoor-planters',
    'window box': '/collections/window-boxes',
    'window boxes': '/collections/window-boxes'
  };

  /* -------------------------------------------------------------- NORMALIZE */

  // Standardize size units: "7 ft" / "7ft" / "7 foot" / "7 feet" -> "7 ft",
  // and inch variants -> "in". Helps size searches behave consistently.
  function normalizeUnits(q) {
    return q
      .replace(/(\d)\s*(?:'|’|foot|feet|ft\.?)\b/g, '$1 ft')
      .replace(/(\d)\s*(?:"|”|inch|inches|in\.?)\b/g, '$1 in');
  }

  // Turn a raw typed query into the best search term we can.
  function normalize(raw) {
    if (!raw) return '';
    var q = String(raw).toLowerCase().trim().replace(/\s+/g, ' ');
    q = normalizeUnits(q);

    // Whole-query synonym (e.g. "wardian case" -> "terrarium")
    if (SYNONYMS[q]) return SYNONYMS[q];

    // Word-by-word: fix typos first, then per-word synonyms.
    var words = q.split(' ').map(function (w) {
      if (TYPOS[w]) w = TYPOS[w];
      if (SYNONYMS[w]) w = SYNONYMS[w];
      return w;
    });
    var out = words.join(' ');

    // Re-check whole-query synonyms after typo correction
    if (SYNONYMS[out]) return SYNONYMS[out];
    return out;
  }

  // If a query is pure category/sale intent, return the collection URL.
  function redirectFor(raw) {
    if (!raw) return null;
    var q = String(raw).toLowerCase().trim().replace(/\s+/g, ' ');
    if (REDIRECTS[q]) return REDIRECTS[q];
    var n = normalize(raw);
    if (REDIRECTS[n]) return REDIRECTS[n];
    return null;
  }

  if (!ENABLED) return;

  /* ----------------------------------------- 1. PREDICTIVE DROPDOWN (fetch) */
  // Rewrite the `q` param on predictive-search requests so the dropdown shows
  // corrected/synonym results as the shopper types.
  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      try {
        var urlStr = (typeof input === 'string') ? input
                    : (input && input.url) ? input.url : null;
        if (urlStr && urlStr.indexOf('/search/suggest') !== -1) {
          var u = new URL(urlStr, window.location.origin);
          var q = u.searchParams.get('q');
          if (q) {
            var nq = normalize(q);
            if (nq && nq !== q.toLowerCase()) {
              u.searchParams.set('q', nq);
              if (typeof input === 'string') {
                input = u.toString();
              } else {
                input = new Request(u.toString(), input);
              }
            }
          }
        }
      } catch (e) { /* never break native fetch */ }
      return origFetch.call(this, input, init);
    };
  }

  /* ------------------------------------------ 2. SEARCH SUBMIT (navigation) */
  // On submit: redirect clear intent, otherwise rewrite q to the corrected
  // term and stash the original so we can show a "Showing results for" note.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.getAttribute) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/search') === -1) return;

    var input = form.querySelector('input[name="q"]');
    if (!input || !input.value.trim()) return;

    var raw = input.value.trim();

    // (a) Strong intent -> straight to collection
    var target = redirectFor(raw);
    if (target) {
      e.preventDefault();
      window.location.assign(target);
      return;
    }

    // (b) Corrected term -> submit corrected, remember the original
    var nq = normalize(raw);
    if (nq && nq !== raw.toLowerCase()) {
      input.value = nq;
      var orig = form.querySelector('input[name="original_q"]');
      if (!orig) {
        orig = document.createElement('input');
        orig.type = 'hidden';
        orig.name = 'original_q';
        form.appendChild(orig);
      }
      orig.value = raw;
    }
  }, true); // capture phase, so we run before Dawn's own handler

  /* --------------------------------------------- 3. "Showing results for X" */
  function showCorrectionBanner() {
    if (!SHOW_CORRECTION_BANNER) return;
    if (window.location.pathname.indexOf('/search') !== 0) return;

    var params = new URLSearchParams(window.location.search);
    var original = params.get('original_q');
    var corrected = params.get('q');
    if (!original || !corrected) return;
    if (original.toLowerCase() === corrected.toLowerCase()) return;

    var results = document.getElementById('ProductGridContainer') ||
                  document.querySelector('#main-search, main, .search__results, [role="main"]');
    if (!results) return;
    if (document.getElementById('smart-search-banner')) return;

    var rawUrl = '/search?q=' + encodeURIComponent(original) + '&options%5Bprefix%5D=last';
    var banner = document.createElement('p');
    banner.id = 'smart-search-banner';
    banner.className = 'smart-search-banner';
    banner.innerHTML =
      'Showing results for <strong>"' + escapeHtml(corrected) + '"</strong>. ' +
      'Search instead for <a href="' + rawUrl + '">"' + escapeHtml(original) + '"</a>.';
    results.insertBefore(banner, results.firstChild);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showCorrectionBanner);
  } else {
    showCorrectionBanner();
  }
})();